/**
 * One place to fire a conversion event, to every destination at once.
 *
 * WHY THIS EXISTS
 * ---------------
 * Three trackers were already installed and none of them measured anything you
 * could act on:
 *   - Meta Pixel (417023674704729) fired PageView and nothing else, so no
 *     campaign could optimise for a purchase or a lead.
 *   - Two separate GA4 properties are live — G-ZD7DB23Q2D via gtag in
 *     index.html, G-1HPYWFY37L via the Firebase SDK — splitting the data.
 *   - The Firebase calls that did exist passed the *product name* as the event
 *     name (`logEvent(analytics, " Sandal_Jar added to cart ")`). GA4 event
 *     names must be <=40 chars of [A-Za-z0-9_] starting with a letter, so those
 *     were dropped, and the ones that survived created a new event type per
 *     product instead of one countable `add_to_cart`.
 *
 * Every call here sends a Meta standard event and a GA4 recommended event under
 * their documented names, with the same value and currency, so Ads Manager and
 * GA4 agree on what a conversion is worth. Both GA4 properties get the event;
 * they are separate properties, so this is not double counting.
 *
 * Nothing here throws. Ad blockers remove fbq and gtag outright, and a tracking
 * failure must never take down a checkout, so every send is guarded and errors
 * are swallowed after a console warning.
 */
import { analytics } from "../config/firebase";
import { logEvent } from "firebase/analytics";

const CURRENCY = "INR";

/** Meta and GA4 disagree on names for the same moment; keep the pairs together. */
const EVENTS = {
  viewItem: { meta: "ViewContent", ga: "view_item" },
  addToCart: { meta: "AddToCart", ga: "add_to_cart" },
  beginCheckout: { meta: "InitiateCheckout", ga: "begin_checkout" },
  purchase: { meta: "Purchase", ga: "purchase" },
  lead: { meta: "Lead", ga: "generate_lead" },
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Normalise a cart line or catalogue entry into the shape both platforms want.
 * Prices in Data.js are strings ("999"), so every number goes through Number().
 */
const lineItem = (item = {}) => ({
  id: item.product_name_key || String(item.id ?? ""),
  name: item.product_name || "",
  quantity: Math.max(toNumber(item.quantity) || 1, 1),
  price: toNumber(item.price),
});

const sendMeta = (name, params) => {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  try {
    window.fbq("track", name, params);
  } catch (err) {
    console.warn("Meta Pixel event failed", name, err);
  }
};

const sendGtag = (name, params) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  try {
    window.gtag("event", name, params);
  } catch (err) {
    console.warn("gtag event failed", name, err);
  }
};

const sendFirebase = (name, params) => {
  if (!analytics) return;
  try {
    logEvent(analytics, name, params);
  } catch (err) {
    console.warn("Firebase Analytics event failed", name, err);
  }
};

/** Fan one conversion out to the pixel and both GA4 properties. */
const track = (key, { metaParams, gaParams }) => {
  const names = EVENTS[key];
  if (!names) return;
  sendMeta(names.meta, metaParams);
  sendGtag(names.ga, gaParams);
  sendFirebase(names.ga, gaParams);
};

/** Someone opened a product page. */
export function trackViewItem(product) {
  const line = lineItem(product);
  track("viewItem", {
    metaParams: {
      content_type: "product",
      content_ids: [line.id],
      content_name: line.name,
      value: line.price,
      currency: CURRENCY,
    },
    gaParams: {
      currency: CURRENCY,
      value: line.price,
      items: [{ item_id: line.id, item_name: line.name, price: line.price }],
    },
  });
}

/** Something went into the basket, from anywhere in the app. */
export function trackAddToCart(item) {
  const line = lineItem(item);
  const value = line.price * line.quantity;
  track("addToCart", {
    metaParams: {
      content_type: "product",
      content_ids: [line.id],
      content_name: line.name,
      contents: [{ id: line.id, quantity: line.quantity }],
      value,
      currency: CURRENCY,
    },
    gaParams: {
      currency: CURRENCY,
      value,
      items: [
        {
          item_id: line.id,
          item_name: line.name,
          price: line.price,
          quantity: line.quantity,
        },
      ],
    },
  });
}

/** The customer committed to checking out — the last step before an order. */
export function trackBeginCheckout(items = [], value) {
  const lines = items.map(lineItem);
  track("beginCheckout", {
    metaParams: {
      content_type: "product",
      content_ids: lines.map((l) => l.id),
      contents: lines.map((l) => ({ id: l.id, quantity: l.quantity })),
      num_items: lines.reduce((n, l) => n + l.quantity, 0),
      value: toNumber(value),
      currency: CURRENCY,
    },
    gaParams: {
      currency: CURRENCY,
      value: toNumber(value),
      items: lines.map((l) => ({
        item_id: l.id,
        item_name: l.name,
        price: l.price,
        quantity: l.quantity,
      })),
    },
  });
}

/**
 * An order is genuinely stored. Called only after the Firestore write resolves,
 * so the revenue in Ads Manager matches the revenue in the order book.
 * `orderId` is the Firestore document id, which makes the purchase deduplicable.
 */
export function trackPurchase({ orderId, items = [], value, shipping = 0 }) {
  const lines = items.map(lineItem);
  track("purchase", {
    metaParams: {
      content_type: "product",
      content_ids: lines.map((l) => l.id),
      contents: lines.map((l) => ({ id: l.id, quantity: l.quantity })),
      num_items: lines.reduce((n, l) => n + l.quantity, 0),
      value: toNumber(value),
      currency: CURRENCY,
      order_id: orderId,
    },
    gaParams: {
      transaction_id: orderId,
      currency: CURRENCY,
      value: toNumber(value),
      shipping: toNumber(shipping),
      items: lines.map((l) => ({
        item_id: l.id,
        item_name: l.name,
        price: l.price,
        quantity: l.quantity,
      })),
    },
  });
}

/**
 * A wholesale enquiry was captured. `value` is the calculator's estimated order
 * value, which is what makes lead quality legible: a 600kg enquiry and a 50kg
 * enquiry both count as one Lead otherwise.
 */
export function trackLead({ value, volumeKg, productFocus } = {}) {
  track("lead", {
    metaParams: {
      content_category: "wholesale",
      content_name: productFocus || "wholesale enquiry",
      value: toNumber(value),
      currency: CURRENCY,
    },
    gaParams: {
      currency: CURRENCY,
      value: toNumber(value),
      lead_source: "wholesale_form",
      estimated_volume_kg: toNumber(volumeKg),
    },
  });
}
